import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { isTauriSync } from "./lib/tauri";
import "./styles.css";

/**
 * Service-worker-beleid, per platform:
 *
 *  - Browser-PWA: registreer 'm (push-notificaties + offline app-shell).
 *    autoUpdate via skipWaiting() in src/sw.ts zorgt dat een nieuwe versie
 *    na één refresh overneemt.
 *
 *  - Tauri-desktop: NIET registreren, en een eventueel eerder
 *    geregistreerde SW + alle workbox-caches actief verwijderen. De
 *    assets zitten al in de binary, dus de SW voegt niks toe en zorgt
 *    juist voor stale UI na een app-update (de SW serveert de oude,
 *    gecachte index.html). Door 'm hier op te ruimen heelt elke desktop-
 *    client zichzelf zodra 'ie deze build één keer draait — daarna kan
 *    geen update meer blijven hangen.
 */
function initServiceWorker(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

  if (isTauriSync()) {
    void (async () => {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
        if ("caches" in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      } catch {
        /* best-effort — een gefaalde opruiming mag de app niet blokkeren */
      }
    })();
    return;
  }

  // Browser: dynamische import zodat het virtual-module nooit in het
  // Tauri-executiepad terechtkomt.
  void import("virtual:pwa-register")
    .then(({ registerSW }) => {
      registerSW({ immediate: true });
    })
    .catch(() => undefined);
}

initServiceWorker();

const root = document.getElementById("root");
if (!root) throw new Error("#root not found");
createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
